import { Bug } from 'lucide-react';
import type { CSSProperties, PointerEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { useOpenOverlay, type OverlayPlacement } from '@/components/FeedbackButton/useOpenOverlay';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip/Tooltip';
import { useShakeReady } from '@/hooks/useShakeReady';
import { openShake } from '@/lib/shake';
import { cn } from '@/util/utils';

/** Moves the button off its corner while a drawer is open; a dialog leaves the corner free. */
function placementStyle(placement: OverlayPlacement): CSSProperties | undefined {
    if (placement.kind === 'beside-drawer') return { right: `calc(${placement.right}px + 1rem)` };
    if (placement.kind === 'above-footer') return { bottom: `calc(${placement.bottom}px + 1rem)` };
    return undefined;
}

/**
 * An open drawer or dialog closes on a press anywhere outside it, and Radix hears that press
 * on `document`. Stopping it at the button keeps the drawer open, with whatever was typed in
 * it, while Shake opens over it.
 */
function keepOverlayOpen(event: PointerEvent): void {
    event.stopPropagation();
}

/*
  Shake's own launcher was a tall pill fixed to the right edge at mid height, above everything,
  and it took the clicks aimed at the controls under it (ALS-329, ADR 0073). This is ours
  instead: a 44px circle in the bottom-right corner, a tap target on a phone as much as on a
  desk. The bug icon and the name "Report a bug with Shake" say what it opens, since the
  circle has no room for the old pill's words.

  z-10 keeps it above the page and under the sidebar backdrop (20), the phone menu (30) and
  toasts. While a drawer or dialog is open it rises above them (z-60) so a bug can be reported
  from inside one (ALS-458, ADR 0096): beside a drawer when there is room on its left, above
  the footer of a drawer that fills the screen or a dialog tall enough to reach the corner,
  and in its corner beside any other dialog. It never sits over Save or Cancel. Radix sets pointer-events: none on the
  page around an open overlay, so the button takes them back for itself. Screen readers and
  the keyboard still can't reach it there, since the overlay hides and traps everything
  outside it; the pointer can.

  It is left out of Shake's screenshot (data-html2canvas-ignore, which the SDK's html2canvas
  skips), so a report shows the screen, not the button that filed it.

  It floats over the page and changes nothing in the layout around it: no page, drawer or
  toast moves or resizes because of it, in any environment. A toast sits above it while it
  shows, and a control in the corner can pass under it as a page scrolls.
*/
export function FeedbackButton() {
    const ready = useShakeReady();
    const placement = useOpenOverlay(ready);

    if (!ready) return null;

    const overOverlay = placement.kind !== 'none';

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant="outline"
                    size="icon"
                    aria-label="Report a bug with Shake"
                    data-html2canvas-ignore
                    onClick={openShake}
                    onPointerDown={overOverlay ? keepOverlayOpen : undefined}
                    style={placementStyle(placement)}
                    className={cn(
                        'fixed right-[calc(1rem+env(safe-area-inset-right))] bottom-[calc(1rem+env(safe-area-inset-bottom))] z-10 size-11 rounded-full shadow-lg print:hidden [&_svg:not([class*=size-])]:size-5',
                        overOverlay && 'pointer-events-auto z-60',
                    )}
                >
                    <Bug aria-hidden />
                </Button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={8} data-html2canvas-ignore>
                Report a bug with Shake
            </TooltipContent>
        </Tooltip>
    );
}
